/**
 * LogoutCoordinator - Prevents concurrent/duplicate logout attempts.
 *
 * Multiple independent systems (api.ts interceptor, authInterceptor.ts,
 * useAuth.ts) can all detect auth failures and attempt to log the user out.
 * Without coordination, these race against each other causing double redirects,
 * double state clearing, and poor UX.
 *
 * This singleton gates all logout actions through a single lock so only the
 * first caller wins.
 *
 * 2026-05-03: removed the 5-second auto-reset. The auto-reset was a "safety
 * net" for the case where a logout flow stalled (redirect blocked, cleanup
 * threw), but in practice it created a re-entry window for 401-storm loops:
 * if cookies expired silently and a page like /onecare/notes was open, 5+
 * concurrent requests would all 401, the first would acquire the lock and
 * trigger refresh + clearAuth + redirect, the lock would auto-release 5s
 * later, and any new 401s in flight (or fired by re-mounted components on
 * the destination page) would acquire the lock AGAIN and run the whole
 * cycle. Net effect: continuous 401 floods on notes/labels/folders/
 * disclaimer/feature-enablement endpoints.
 *
 * The lock now persists until either (a) LogoutRedirectHandler completes
 * the navigation and explicitly calls reset(), or (b) the user
 * successfully logs in (loginAsync.fulfilled / completeTwoStepLogin).
 */
class LogoutCoordinatorClass {
  private isLoggingOut = false;

  /**
   * Request permission to perform a logout.
   * Returns true if this caller should proceed, false if another logout is already in progress.
   */
  requestLogout(reason: string, source: string): boolean {
    if (this.isLoggingOut) {
      console.warn(
        `[LOGOUT-COORD] Duplicate logout rejected | source=${source} reason=${reason} | ` +
        `timestamp=${new Date().toISOString()}`
      );
      return false;
    }

    this.isLoggingOut = true;
    console.warn(
      `[LOGOUT-COORD] Logout granted | source=${source} reason=${reason} | ` +
      `timestamp=${new Date().toISOString()}`
    );

    return true;
  }

  /**
   * Reset the coordinator state. Call after the redirect to login completes
   * OR after a successful new login.
   */
  reset(): void {
    this.isLoggingOut = false;
  }

  /**
   * Check if a logout is currently in progress.
   *
   * Callers should consult this before attempting a token refresh or any
   * authenticated request — once a logout is in flight, the user's auth
   * state is being torn down and any in-flight 401 should reject silently
   * rather than queue another refresh / logout cycle.
   */
  get active(): boolean {
    return this.isLoggingOut;
  }
}

export const LogoutCoordinator = new LogoutCoordinatorClass();
